import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from '@/api/kindergarten';
import type {
  KgChildPayload,
  KgEnrollPayload,
  KgEnrollmentUpdatePayload,
  KgFamilyFilters,
  KgFamilyPayload,
  KgGroupPayload,
  KgGuardianPayload,
  KgRolloverPayload,
  KgSchoolYearPayload,
} from '@/types/kindergarten';

export const kgKeys = {
  all: ['kindergarten'] as const,
  years: () => [...kgKeys.all, 'school-years'] as const,
  year: (id: string) => [...kgKeys.years(), id] as const,
  groups: (yearId?: string) => [...kgKeys.all, 'groups', yearId ?? 'current'] as const,
  group: (id: string) => [...kgKeys.all, 'group', id] as const,
  roster: (id: string, status?: string) => [...kgKeys.group(id), 'roster', status ?? 'all'] as const,
  rolloverPreview: (fromId: string, toId: string) => [...kgKeys.all, 'rollover-preview', fromId, toId] as const,
  families: (filters: KgFamilyFilters) => [...kgKeys.all, 'families', filters] as const,
  family: (id: string) => [...kgKeys.all, 'family', id] as const,
  child: (id: string) => [...kgKeys.all, 'child', id] as const,
  betelFamily: (id: string) => [...kgKeys.all, 'betel-family', id] as const,
  member: (id: string) => [...kgKeys.all, 'member', id] as const,
};

/** Invalidate everything kindergarten — cheap, and the module is small. */
function useInvalidateKg() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: kgKeys.all });
}

// ─── School years ───

export function useSchoolYears() {
  return useQuery({ queryKey: kgKeys.years(), queryFn: api.listSchoolYears, staleTime: 5 * 60_000 });
}

export function useCurrentSchoolYear() {
  const q = useSchoolYears();
  return { ...q, data: q.data?.find((y) => y.is_current) ?? null };
}

export function useSchoolYear(id?: string) {
  return useQuery({ queryKey: kgKeys.year(id ?? ''), queryFn: () => api.getSchoolYear(id!), enabled: !!id });
}

export function useCreateSchoolYear() {
  const invalidate = useInvalidateKg();
  return useMutation({ mutationFn: (p: KgSchoolYearPayload) => api.createSchoolYear(p), onSuccess: invalidate });
}

export function useUpdateSchoolYear() {
  const invalidate = useInvalidateKg();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<KgSchoolYearPayload> }) => api.updateSchoolYear(id, payload),
    onSuccess: invalidate,
  });
}

export function useMakeSchoolYearCurrent() {
  const invalidate = useInvalidateKg();
  return useMutation({ mutationFn: (id: string) => api.makeSchoolYearCurrent(id), onSuccess: invalidate });
}

export function useDeleteSchoolYear() {
  const invalidate = useInvalidateKg();
  return useMutation({ mutationFn: (id: string) => api.deleteSchoolYear(id), onSuccess: invalidate });
}

// ─── Rollover ("Pornire an școlar") ───

export function useRolloverPreview(fromId?: string, toSchoolYearId?: string) {
  return useQuery({
    queryKey: kgKeys.rolloverPreview(fromId ?? '', toSchoolYearId ?? ''),
    queryFn: () => api.previewRollover(fromId!, toSchoolYearId!),
    enabled: !!fromId && !!toSchoolYearId,
  });
}

export function useCreateRolloverGroups() {
  const invalidate = useInvalidateKg();
  return useMutation({
    mutationFn: ({ fromId, toSchoolYearId }: { fromId: string; toSchoolYearId: string }) =>
      api.createRolloverGroups(fromId, toSchoolYearId),
    onSuccess: invalidate,
  });
}

export function useRunRollover() {
  const invalidate = useInvalidateKg();
  return useMutation({
    mutationFn: ({ fromId, payload }: { fromId: string; payload: KgRolloverPayload }) => api.runRollover(fromId, payload),
    onSuccess: invalidate,
  });
}

// ─── Groups ───

export function useGroups(schoolYearId?: string) {
  return useQuery({ queryKey: kgKeys.groups(schoolYearId), queryFn: () => api.listGroups(schoolYearId) });
}

export function useGroup(id?: string) {
  return useQuery({ queryKey: kgKeys.group(id ?? ''), queryFn: () => api.getGroup(id!), enabled: !!id });
}

export function useGroupRoster(id?: string, status?: string) {
  return useQuery({ queryKey: kgKeys.roster(id ?? '', status), queryFn: () => api.getGroupRoster(id!, status), enabled: !!id });
}

export function useCreateGroup() {
  const invalidate = useInvalidateKg();
  return useMutation({ mutationFn: (p: KgGroupPayload) => api.createGroup(p), onSuccess: invalidate });
}

export function useUpdateGroup() {
  const invalidate = useInvalidateKg();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<KgGroupPayload> }) => api.updateGroup(id, payload),
    onSuccess: invalidate,
  });
}

export function useDeleteGroup() {
  const invalidate = useInvalidateKg();
  return useMutation({ mutationFn: (id: string) => api.deleteGroup(id), onSuccess: invalidate });
}

// ─── Families ───

export function useFamilies(filters: KgFamilyFilters) {
  return useQuery({ queryKey: kgKeys.families(filters), queryFn: () => api.listFamilies(filters), placeholderData: (prev) => prev });
}

export function useFamily(id?: string) {
  return useQuery({ queryKey: kgKeys.family(id ?? ''), queryFn: () => api.getFamily(id!), enabled: !!id });
}

export function useCreateFamily() {
  const invalidate = useInvalidateKg();
  return useMutation({ mutationFn: (p: KgFamilyPayload) => api.createFamily(p), onSuccess: invalidate });
}

export function useUpdateFamily() {
  const invalidate = useInvalidateKg();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<KgFamilyPayload> }) => api.updateFamily(id, payload),
    onSuccess: invalidate,
  });
}

export function useDeleteFamily() {
  const invalidate = useInvalidateKg();
  return useMutation({ mutationFn: (id: string) => api.deleteFamily(id), onSuccess: invalidate });
}

export function useLinkBetelFamily() {
  const invalidate = useInvalidateKg();
  return useMutation({
    mutationFn: ({ id, betelFamilyId }: { id: string; betelFamilyId: string | null }) => api.linkBetelFamily(id, betelFamilyId),
    onSuccess: invalidate,
  });
}

// ─── Guardians ───

export function useAddGuardian() {
  const invalidate = useInvalidateKg();
  return useMutation({
    mutationFn: ({ familyId, payload }: { familyId: string; payload: Parameters<typeof api.addGuardian>[1] }) => api.addGuardian(familyId, payload),
    onSuccess: invalidate,
  });
}

export function useUpdateGuardian() {
  const invalidate = useInvalidateKg();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<KgGuardianPayload> & { is_active?: boolean; member_id?: string | null } }) =>
      api.updateGuardian(id, payload),
    onSuccess: invalidate,
  });
}

export function useUpdateGuardianPivot() {
  const invalidate = useInvalidateKg();
  return useMutation({
    mutationFn: ({ familyId, guardianId, payload }: { familyId: string; guardianId: string; payload: Parameters<typeof api.updateGuardianPivot>[2] }) =>
      api.updateGuardianPivot(familyId, guardianId, payload),
    onSuccess: invalidate,
  });
}

export function useDetachGuardian() {
  const invalidate = useInvalidateKg();
  return useMutation({
    mutationFn: ({ familyId, guardianId }: { familyId: string; guardianId: string }) => api.detachGuardian(familyId, guardianId),
    onSuccess: invalidate,
  });
}

// ─── Children + enrollments ───

export function useChild(id?: string) {
  return useQuery({ queryKey: kgKeys.child(id ?? ''), queryFn: () => api.getChild(id!), enabled: !!id });
}

export function useAddChild() {
  const invalidate = useInvalidateKg();
  return useMutation({
    mutationFn: ({ familyId, payload }: { familyId: string; payload: KgChildPayload }) => api.addChild(familyId, payload),
    onSuccess: invalidate,
  });
}

export function useUpdateChild() {
  const invalidate = useInvalidateKg();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<KgChildPayload> }) => api.updateChild(id, payload),
    onSuccess: invalidate,
  });
}

export function useDeleteChild() {
  const invalidate = useInvalidateKg();
  return useMutation({ mutationFn: (id: string) => api.deleteChild(id), onSuccess: invalidate });
}

export function useEnrollChild() {
  const invalidate = useInvalidateKg();
  return useMutation({
    mutationFn: ({ childId, payload }: { childId: string; payload: KgEnrollPayload }) => api.enrollChild(childId, payload),
    onSuccess: invalidate,
  });
}

export function useUpdateEnrollment() {
  const invalidate = useInvalidateKg();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: KgEnrollmentUpdatePayload }) => api.updateEnrollment(id, payload),
    onSuccess: invalidate,
  });
}

export function useWithdrawEnrollment() {
  const invalidate = useInvalidateKg();
  return useMutation({
    mutationFn: ({ id, withdrawnOn, notes }: { id: string; withdrawnOn: string; notes?: string }) => api.withdrawEnrollment(id, withdrawnOn, notes),
    onSuccess: invalidate,
  });
}

export function useReactivateEnrollment() {
  const invalidate = useInvalidateKg();
  return useMutation({ mutationFn: (id: string) => api.reactivateEnrollment(id), onSuccess: invalidate });
}

// ─── CRM links ───

export function useKindergartenForBetelFamily(familyId?: string, enabled = true) {
  return useQuery({
    queryKey: kgKeys.betelFamily(familyId ?? ''),
    queryFn: () => api.getKindergartenForBetelFamily(familyId!),
    enabled: !!familyId && enabled,
  });
}

export function useKindergartenForMember(memberId?: string, enabled = true) {
  return useQuery({
    queryKey: kgKeys.member(memberId ?? ''),
    queryFn: () => api.getKindergartenForMember(memberId!),
    enabled: !!memberId && enabled,
  });
}
